import React from "react";
import { Check } from "lucide-react";

import {
  deadlineUrgency,
  formatDaysRemaining,
  formatDeadline,
} from "../../../services/tendersApi";
import {
  DocumentCount,
  NoticePill,
  SetAsidePill,
  SourceBadge,
  URGENCY_TEXT,
} from "./TenderUI";

// The matched sentence from the notice, with the search terms marked. The
// server wraps them in [[hl]]...[[/hl]] rather than HTML, so this splits on the
// markers and builds the elements here. Nothing from a government feed is ever
// handed to the browser as markup.
const HIGHLIGHT = /\[\[hl\]\]([\s\S]*?)\[\[\/hl\]\]/g;

export function Snippet({ text }) {
  // A match in the title leaves the description with nothing to highlight, and
  // Postgres hands back its opening sentence regardless. Shown, that reads as
  // the reason the row matched when it is just the first thing in the notice.
  // No marker means no explanation to give, so say nothing.
  if (!text || !text.includes("[[hl]]")) return null;

  const parts = [];
  let cursor = 0;
  for (const match of text.matchAll(HIGHLIGHT)) {
    if (match.index > cursor) parts.push(text.slice(cursor, match.index));
    parts.push(
      <mark key={match.index} className="bg-[#FCEFAF] text-[#18101C] rounded-[2px] px-[1px]">
        {match[1]}
      </mark>
    );
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));

  return (
    <p className="text-[13px] text-[#52525B] leading-[1.5] mt-1.5 line-clamp-2">
      &hellip;{parts}&hellip;
    </p>
  );
}

// One tender in the results list. Two zones: the tender itself on the left,
// the deadline alone on the right behind a hairline so the eye can scan a
// column of dates without reading a single title.
export default function TenderRow({ tender, onOpen }) {
  const urgency = deadlineUrgency(tender);
  const open = () => onOpen(tender);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && open()}
      className="group font-outfit border border-[#E5E7EB] rounded-xl bg-white px-[18px] py-4 mb-2.5
                 flex gap-5 items-stretch cursor-pointer transition-all
                 hover:border-[#C95EBE] hover:shadow-[0_2px_10px_rgba(201,94,190,0.10)]"
    >
      <div className="flex-1 min-w-0">
        <div className="flex gap-[7px] flex-wrap items-center mb-2">
          <SourceBadge label={tender.source_label} />
          <NoticePill type={tender.notice_type} label={tender.notice_type_label} />
          <SetAsidePill label={tender.set_aside_label} />
        </div>

        <h3 className="text-[15px] font-semibold text-[#18101C] leading-[1.35]
                       group-hover:text-[#BB42AE] transition-colors mb-1">
          {tender.title}
        </h3>

        <p className="text-[13px] text-[#71717A] truncate">
          {[tender.buyer_path || tender.buyer, tender.place, tender.reference]
            .filter(Boolean)
            .join(" · ")}
        </p>

        {/* Why this row is here. Half the matches for a term like "AI" have it
            nowhere else on the card, so without this the list reads as random. */}
        <Snippet text={tender.snippet} />

        <div className="flex gap-3.5 flex-wrap items-center mt-2.5">
          <DocumentCount
            hasDocuments={tender.has_documents}
            count={tender.document_count}
            linkCount={tender.link_count}
          />

          {/* A colleague's import shows too — the corpus is shared across the
              workspace, so this stops two people bidding the same contract twice. */}
          {tender.is_imported && (
            <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#16A34A]">
              <Check className="w-3 h-3" strokeWidth={2.5} />
              Already in a project
            </span>
          )}
        </div>
      </div>

      <div className="shrink-0 min-w-[122px] pl-5 border-l border-[#F1F1F3]
                      flex flex-col items-end justify-center text-right">
        <span className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">
          Closes
        </span>
        <span className={`text-[14px] font-semibold whitespace-nowrap leading-tight ${URGENCY_TEXT[urgency]}`}>
          {formatDaysRemaining(tender) || "No deadline"}
        </span>
        {/* formatDeadline drops the time when the source published no timezone —
            a quarter of federal tenders. Inventing one is how a bid gets
            submitted late. */}
        <span className="text-[11px] text-[#9CA3AF] mt-0.5">{formatDeadline(tender)}</span>
      </div>
    </div>
  );
}
